import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BeingSnapshot, BeingTelemetryService } from '../../services/being/being-telemetry.service';
import { BehaviorSubject, combineLatest, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { CommonModule } from '@angular/common';

// ================================================================
// BEING VITALS — drive traces drawn straight onto the map
// ================================================================
//
// No panel, no card, no border. The traces ARE the UI. A scrim gradient
// sits behind them so they stay readable over bright map tiles without
// boxing them in.
//
// Two lines share one 0–1 axis: energy (blue) and hunger (orange),
// reusing the colours already in the map chrome so this reads as part of
// the same instrument rather than a widget dropped on top.
//
// The rest threshold is drawn as a hairline because it's the line that
// actually explains the shape of the trace — when energy crosses it the
// being stops walking. A gridline that encodes a rule is worth drawing;
// decorative gridlines aren't.
// ================================================================

interface Trace {
  key: "energy" | "hunger";
  colour: string;
  points: string;
  current: number;
  last: { x: number; y: number } | null;
}

interface Vitals {
  name: string;
  alive: boolean;
  causeOfDeath: string | null;
  traces: Trace[];
  resting: boolean;
  critical: boolean;
  spanHours: number;
  empty: boolean;
  thought: string;
  ticks: number[]
}



const W = 1000;
const H = 100;
const REST_THRESHOLD = 0.25;
const TICK_MS = 5 * 60_000;




@Component({
  selector: 'app-being-vitals',
  standalone: true,
  imports: [
    CommonModule
  ],
  templateUrl: './being-vitals.component.html',
  styleUrl: './being-vitals.component.scss'
})





export class BeingVitalsComponent implements OnChanges {






  private telemetry = inject(
    BeingTelemetryService
  );

  /** Which being to trace. Null hides the overlay. */
  @Input() beingId: string | null = null;

  /** How many snapshots to plot. 288 ≈ 24h at a 5-minute tick. */
  @Input() points = 96;

  /** Visible time window in hours. 3h ≈ 36 ticks — legible at 1000px wide. */
  @Input() windowHours = 3;

  readonly W = W;
  readonly viewBox = `0 0 ${W} ${H}`;
  readonly thresholdY = (1 - REST_THRESHOLD) * H;
  readonly H = H;

  private points$ = new BehaviorSubject<number>(96);
  private beingId$ = new BehaviorSubject<string | null>(null);
  private windowHours$ = new BehaviorSubject<number>(3);


  vitals$: Observable<Vitals | null> = combineLatest([this.beingId$, this.windowHours$]).pipe(
    switchMap(([id, hours]) => {
      if (!id) return of(null);
      const points = Math.ceil((hours * 3600_000) / TICK_MS) + 2;   // +2 so the edge scrolls cleanly
      return combineLatest([
        this.telemetry.history$(id, points),
        this.telemetry.being$(id),
      ]).pipe(
        map(([snaps, being]) => {
          const v = this.toVitals(snaps, hours * 3600_000);
          return v && { ...v, thought: being?.lastThought?.text ?? "" };
        }),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["beingId"]) this.beingId$.next(this.beingId);
    if (changes["windowHours"]) this.windowHours$.next(this.windowHours);
  }

  private toVitals(snaps: BeingSnapshot[], windowMs: number): Vitals {
    if (snaps.length === 0) {
      return {
        name: "", alive: true, causeOfDeath: null,
        traces: [], ticks: [],
        resting: false, critical: false,
        spanHours: windowMs / 3_600_000,
        empty: true, thought: "",
      };
    }

    const latest = snaps[snaps.length - 1];
    const now = Date.now();
    const xOf = (ts: number) => W - ((now - ts) / windowMs) * W;

    const visible = snaps.filter(s => now - s.ts <= windowMs);

    const traces: Trace[] = ([
      { key: "energy", colour: "#3b82f6" },
      { key: "hunger", colour: "#f15428" },
    ] as const).map(({ key, colour }) => {
      const coords = visible.map(s => ({
        x: xOf(s.ts),
        y: (1 - clamp01(s[key])) * H,
      }));
      return {
        key, colour,
        points: coords.map(c => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" "),
        current: latest[key],
        last: coords[coords.length - 1] ?? null,
      };
    });

    // Hour boundaries — the only x reference that means anything.
    const ticks: number[] = [];
    const firstHour = Math.ceil((now - windowMs) / 3600_000) * 3600_000;
    for (let t = firstHour; t <= now; t += 3600_000) ticks.push(xOf(t));

    return {
      name: latest.beingName,
      alive: latest.alive,
      causeOfDeath: latest.causeOfDeath,
      traces,
      ticks,
      resting: latest.resting,
      critical: latest.energy < 0.15 || latest.hunger > 0.85,
      spanHours: windowMs / 3_600_000,
      empty: false,
      thought: "",
    };
  }
}

function clamp01(n: number | undefined): number {
  return Math.max(0, Math.min(1, n ?? 0));
}